import { useCallback, useEffect, useState } from 'react';

import { isBefore } from 'date-fns';

import { warn } from '@adsk/alloy-react-helpers';

import { getRangeValue, isPropControlled } from '../helpers';
import { DateRangePickerArgs, UpdateActiveMonths } from '../types';

function useDateRangeSelection({
  initialStartDate,
  initialEndDate,
  propsStartDate,
  propsEndDate,
  propsOnChange,
  allowSameStartAndEndDate,
  onFinalSelection,
  setShouldShowSelection,
}: Pick<
  DateRangePickerArgs,
  'initialStartDate' | 'initialEndDate' | 'allowSameStartAndEndDate'
> & {
  propsStartDate?: DateRangePickerArgs['startDate'];
  propsEndDate?: DateRangePickerArgs['endDate'];
  propsOnChange?: DateRangePickerArgs['onChange'];
  onFinalSelection: () => void;
  setShouldShowSelection: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [stateStartDate, setStateStartDate] = useState(initialStartDate);
  const [stateEndDate, setStateEndDate] = useState(initialEndDate);

  const startDate = isPropControlled(propsStartDate)
    ? propsStartDate
    : stateStartDate;
  const endDate = isPropControlled(propsEndDate) ? propsEndDate : stateEndDate;

  const isControlled =
    isPropControlled(propsStartDate) || isPropControlled(propsEndDate);

  useEffect(() => {
    if (isControlled && endDate && startDate && isBefore(endDate, startDate)) {
      warn(
        'DateRangePicker: provided `endDate` is before provided `startDate` - this usage is not supported.',
      );
    }
  }, [isControlled, endDate, startDate]);

  const onDateSelected = useCallback(
    (
      {
        selectable,
        date: newlySelectedDate,
      }: { selectable?: boolean; date: Date },
      updateActiveMonths: UpdateActiveMonths,
    ) => {
      if (!selectable) {
        return;
      } else if (isControlled) {
        setShouldShowSelection(true);
        const selectedDates = getRangeValue({
          newlySelectedDate,
          startDate: propsStartDate,
          endDate: propsEndDate,
          allowSameStartAndEndDate,
        });

        propsOnChange?.(selectedDates);

        const { startDate: nextStartDate, endDate: nextEndDate } =
          selectedDates;

        if (nextStartDate && nextEndDate) {
          onFinalSelection();
          updateActiveMonths(true, null, nextStartDate, nextEndDate);
        }
      } else {
        const selectedDates = getRangeValue({
          newlySelectedDate,
          startDate: stateStartDate,
          endDate: stateEndDate,
          allowSameStartAndEndDate,
        });
        setShouldShowSelection(true);
        propsOnChange?.(selectedDates);

        const { startDate: nextStartDate, endDate: nextEndDate } =
          selectedDates;

        setStateStartDate(nextStartDate);
        setStateEndDate(nextEndDate);

        if (nextStartDate && nextEndDate) {
          onFinalSelection();
          updateActiveMonths(true, null, nextStartDate, nextEndDate);
        }
      }
    },
    [
      allowSameStartAndEndDate,
      isControlled,
      propsEndDate,
      propsStartDate,
      stateEndDate,
      stateStartDate,
      propsOnChange,
      onFinalSelection,
      setShouldShowSelection,
    ],
  );

  const resetSelection = useCallback(() => {
    propsOnChange?.({ startDate: null, endDate: null });

    if (!isControlled) {
      setStateStartDate(null);
      setStateEndDate(null);
    }
  }, [isControlled, propsOnChange]);

  return {
    startDate,
    endDate,
    onDateSelected,
    resetSelection,
  };
}

export default useDateRangeSelection;
